import { Controller } from "stimulus";
import { openDisclosure, toggleDisclosure, closeOverlay, isOpen } from "../../training_grooming_calendar/overlay";
import { resetDetail, activeCount, resettable } from "../../training_grooming_calendar/url_state";
import { COLLAPSED, SERVICES_CLOSED, toggleSection, claimReopen, menuMaxHeight, scrollToShow, serviceSummary, serviceMatches, statusSummary, categorySummary, checkedAfterReset, facilitySelection, showAllFocusKey } from "../../training_grooming_calendar/filter_sections";

let pending = null;
let sections = COLLAPSED;

const stateAttr = (element) => (element.hasAttribute("aria-pressed") ? "aria-pressed" : "aria-checked");
const isOn = (element) => element.getAttribute(stateAttr(element)) === "true";
const setOn = (element, on) => element.setAttribute(stateAttr(element), String(on));

function selectedFacility(menu) {
  const option = menu.querySelector('[data-name="facility"][aria-selected="true"]');
  return option ? option.dataset.value : null;
}

function selectFacility(root, value) {
  const options = Array.from(root.querySelectorAll('[data-name="facility"]'));
  const name = (option) => option.querySelector(".tg-facility-option-name");
  const view = facilitySelection(options.map((option) => ({ value: option.dataset.value, label: name(option) ? name(option).textContent : "" })), value);
  if (!view) return;
  options.forEach((option, index) => option.setAttribute("aria-selected", String(view.selected[index])));
  const label = root.querySelector(".tg-facility-value");
  if (label) label.textContent = view.label;
}

export default class extends Controller {
  static targets = ["button", "menu"];

  connect() {
    const loaded = this.element.closest("[data-tg-load-reason]");
    const claim = claimReopen(pending, loaded ? loaded.dataset.tgLoadReason : null);
    pending = null;
    sections = claim.sections;
    this.applySections();
    if (!claim.open) return;
    this.open();
    this.restoreServices(claim.services);
    const focus = claim.focusKey && this.menuTarget.querySelector(`[data-focus-key="${claim.focusKey}"]`);
    if (focus) focus.focus({ preventScroll: true });
    const list = this.categoryList();
    if (list) list.scrollTop = claim.listScroll;
  }

  refreshCounts(state) {
    const count = activeCount(state, this.hasCustomOrder());
    const badge = this.element.querySelector(".tg-filters-count");
    if (badge) {
      badge.textContent = String(count);
      badge.dataset.count = String(count);
      badge.setAttribute("aria-label", `${count} active`);
      badge.hidden = count === 0;
    }
    this.buttonTarget.classList.toggle("tg-filters-active", count > 0);
    this.element.querySelectorAll(".tg-filters-reset-all").forEach((button) => {
      button.hidden = !resettable(state);
    });
    const showAll = this.element.querySelector(".tg-filters-show-all");
    if (showAll) showAll.hidden = (state.hiddenStatuses || []).length === 0;
    const clear = this.element.querySelector(".tg-filters-clear");
    if (clear) {
      const checked = (state.categories || []).length;
      clear.textContent = `Clear (${checked})`;
      clear.hidden = checked === 0;
    }
  }

  hasCustomOrder() {
    const reset = this.element.querySelector(".tg-filters-reset");
    return !!reset && !reset.hidden;
  }

  categoryList() {
    return this.menuTarget.querySelector(".tg-filters-categories");
  }

  listScroll() {
    const list = this.categoryList();
    return list ? list.scrollTop : 0;
  }

  disconnect() {
    if (isOpen(this.menuTarget)) closeOverlay({ restoreFocus: false });
  }

  toggle() {
    if (!toggleDisclosure(this.disclosure())) return;
    this.setServicesOpen(false);
    this.fitMenu();
  }

  open() {
    openDisclosure(this.disclosure());
    this.fitMenu();
  }

  scrolled() {
    this.menuTarget.classList.toggle("tg-filters-scrolled", this.menuTarget.scrollTop > 0);
  }

  fitMenu() {
    if (this.element.classList.contains("tg-filters-sheet")) return;
    const top = this.menuTarget.getBoundingClientRect().top;
    this.menuTarget.style.setProperty("--tg-filters-max", `${menuMaxHeight({ top, viewport: window.innerHeight })}px`);
  }

  disclosure() {
    return {
      element: this.menuTarget,
      trigger: this.buttonTarget,
      root: this.element.closest(".tg"),
      sheet: this.element.classList.contains("tg-filters-sheet"),
      triggerClass: "tg-filters-open",
    };
  }

  close() {
    closeOverlay();
  }

  pick(event) {
    const { name, value } = event.currentTarget.dataset;
    this.menuTarget.querySelectorAll(`[data-name="${name}"]`).forEach((option) => option.setAttribute("aria-checked", option.dataset.value === value ? "true" : "false"));
    this.change(event.currentTarget);
  }

  servicesPanel() {
    return this.menuTarget.querySelector(".tg-svc-panel");
  }

  servicesTrigger() {
    return this.menuTarget.querySelector(".tg-svc-trigger");
  }

  servicesSearch() {
    return this.menuTarget.querySelector(".tg-svc-search-input");
  }

  servicesList() {
    return this.menuTarget.querySelector(".tg-svc-list");
  }

  toggleServices() {
    const panel = this.servicesPanel();
    if (!panel) return;
    const opening = panel.hidden;
    this.setServicesOpen(opening);
    const focus = opening ? this.servicesSearch() : this.servicesTrigger();
    if (focus) focus.focus({ preventScroll: true });
  }

  setServicesOpen(open) {
    const panel = this.servicesPanel();
    if (!panel) return;
    panel.hidden = !open;
    const trigger = this.servicesTrigger();
    if (trigger) trigger.setAttribute("aria-expanded", String(open));
  }

  servicesState() {
    const panel = this.servicesPanel();
    if (!panel || panel.hidden) return SERVICES_CLOSED;
    const search = this.servicesSearch();
    const list = this.servicesList();
    return { open: true, query: search ? search.value : "", scroll: list ? list.scrollTop : 0 };
  }

  restoreServices(state) {
    if (!state.open) return;
    this.setServicesOpen(true);
    const search = this.servicesSearch();
    if (search) search.value = state.query;
    this.filterServices(state.query);
    const list = this.servicesList();
    if (list) list.scrollTop = state.scroll;
  }

  searchServices(event) {
    this.filterServices(event.currentTarget.value);
  }

  filterServices(query) {
    let any = false;
    this.menuTarget.querySelectorAll(".tg-svc-group").forEach((group) => {
      let shown = false;
      group.querySelectorAll('[data-name="service"]').forEach((row) => {
        row.hidden = !serviceMatches(row.dataset.search, query);
        if (!row.hidden) shown = true;
      });
      group.hidden = !shown;
      if (shown) any = true;
    });
    const empty = this.menuTarget.querySelector(".tg-svc-empty");
    if (empty) empty.hidden = any;
  }

  selectAllServices(event) {
    this.setAllServices(true, event.currentTarget);
  }

  deselectAllServices(event) {
    this.setAllServices(false, event.currentTarget);
  }

  setAllServices(on, source) {
    this.menuTarget.querySelectorAll('[data-name="service"]').forEach((row) => setOn(row, on));
    this.change(source);
  }

  refreshServiceSummary() {
    const rows = Array.from(this.menuTarget.querySelectorAll('[data-name="service"]'));
    const name = (row) => row.querySelector(".tg-svc-name");
    const view = serviceSummary(rows.map((row) => ({ label: name(row) ? name(row).textContent : "", checked: isOn(row) })));
    this.menuTarget.querySelectorAll(".tg-svc-summary").forEach((text) => {
      text.textContent = view.label;
    });
    const trigger = this.servicesTrigger();
    if (trigger) trigger.classList.toggle("tg-svc-active", view.active);
    const reset = this.menuTarget.querySelector(".tg-svc-reset");
    if (reset) reset.hidden = !view.active;
  }

  refreshSummaries() {
    this.refreshServiceSummary();
    this.refreshSummary("status", "statuses", statusSummary);
    this.refreshSummary("category", "categories", categorySummary);
  }

  toggleSection(event) {
    const section = event.currentTarget.dataset.sectionToggle;
    sections = toggleSection(sections, section);
    this.applySections();
    const toggle = this.menuTarget.querySelector(`.tg-filters-change[data-section-toggle="${section}"]`);
    if (toggle) toggle.focus({ preventScroll: true });
    if (!sections[section]) return;
    const element = this.menuTarget.querySelector(`[data-filters-section="${section}"]`);
    if (element) this.menuTarget.scrollTop += scrollToShow(element.getBoundingClientRect(), this.menuTarget.getBoundingClientRect());
  }

  applySections() {
    this.element.querySelectorAll("[data-filters-section]").forEach((element) => {
      const open = !!sections[element.dataset.filtersSection];
      element.classList.toggle("tg-section-open", open);
      element.querySelectorAll("[data-section-toggle]").forEach((toggle) => toggle.setAttribute("aria-expanded", open ? "true" : "false"));
    });
  }

  flip(event) {
    const toggle = event.currentTarget;
    setOn(toggle, !isOn(toggle));
    this.change(toggle);
  }

  pickFacility(event) {
    selectFacility(this.menuTarget, event.currentTarget.dataset.value);
    this.change(event.currentTarget, "facility");
  }

  showAllStatuses(event) {
    const rows = this.menuTarget.querySelectorAll('[data-name="status"]');
    rows.forEach((option) => option.setAttribute("aria-checked", "true"));
    this.change(event.currentTarget, showAllFocusKey(!!sections.statuses, rows[0] && rows[0].dataset.focusKey));
  }

  refreshSummary(name, section, summarize) {
    const rows = Array.from(this.menuTarget.querySelectorAll(`[data-name="${name}"]`));
    const text = this.menuTarget.querySelector(`.tg-filters-summary[data-section-toggle="${section}"] .tg-filters-summary-text`);
    if (rows.length === 0 || !text) return;
    const label = (row) => row.querySelector(".tg-filters-option-label");
    const view = summarize(rows.map((row) => ({ label: label(row) ? label(row).textContent : "", checked: isOn(row) })));
    text.textContent = view.label;
    text.classList.toggle("tg-filters-summary-active", view.active);
    if (view.shown) this.showStatusIcons(text, view.shown.map((index) => rows[index]));
  }

  showStatusIcons(text, rows) {
    const generic = text.parentElement.querySelector(".tg-filters-summary-icon");
    if (generic) generic.hidden = rows.length > 0;
    if (rows.length === 0) return;
    text.textContent = "";
    rows.forEach((row, index) => {
      if (index > 0) text.append(", ");
      const item = document.createElement("span");
      item.className = "tg-filters-summary-status";
      const icon = row.querySelector(".tg-status");
      if (icon) item.append(icon.cloneNode(true));
      const label = row.querySelector(".tg-filters-option-label");
      item.append(label ? label.textContent.trim() : "");
      text.append(item);
    });
  }

  clearCategories(event) {
    this.menuTarget.querySelectorAll('[data-name="category"]').forEach((option) => setOn(option, false));
    this.change(event.currentTarget);
  }

  resetAll() {
    const detail = resetDetail();
    this.menuTarget.querySelectorAll("[data-name]").forEach((row) => {
      const checked = checkedAfterReset(detail, row.dataset.name, row.dataset.value);
      if (checked !== null) setOn(row, checked);
    });
    selectFacility(this.menuTarget, detail.facility);
    sections = COLLAPSED;
    this.applySections();
    this.refreshCounts(detail);
    this.refreshSummaries();
    pending = { focusKey: "lanes-all", sections, listScroll: 0 };
    this.element.dispatchEvent(new CustomEvent("training-grooming-filters:change", { bubbles: true, detail }));
  }

  resetOrder(event) {
    pending = { focusKey: "lanes-all", sections, listScroll: this.listScroll() };
    event.currentTarget.dispatchEvent(new CustomEvent("training-grooming-filters:reset-order", { bubbles: true }));
  }

  change(source, focusKey = source.dataset.focusKey) {
    const picked = (name) => this.menuTarget.querySelector(`[data-name="${name}"][aria-checked="true"]`);
    const on = (name) => picked(name) !== null;
    const rows = (name) => Array.from(this.menuTarget.querySelectorAll(`[data-name="${name}"]`));
    const detail = {
      lanes: picked("lanes").dataset.value,
      hiddenServices: rows("service").filter((row) => !isOn(row)).map((row) => row.dataset.value),
      blocks: on("blocks") ? "show" : "hide",
      schedules: on("schedules") ? "show" : "hide",
      scheduled: on("scheduled") ? "only" : "all",
      empty: on("empty") ? "hide" : "show",
      facility: selectedFacility(this.menuTarget),
      categories: rows("category").filter(isOn).map((row) => row.dataset.value),
      hiddenStatuses: Array.from(this.menuTarget.querySelectorAll('[data-name="status"][aria-checked="false"]')).map((option) => option.dataset.value),
    };
    pending = { focusKey, sections, listScroll: this.listScroll(), services: this.servicesState() };
    this.refreshCounts(detail);
    this.refreshSummaries();
    this.element.dispatchEvent(new CustomEvent("training-grooming-filters:change", { bubbles: true, detail }));
  }
}
